import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  NOTIFICATION_PAGE_SIZE,
  deleteNotification,
  getNotificationCount,
  getNotifications,
  markNotificationsRead,
  notificationKeys,
  type FollowUpDue,
  type NotificationPage,
} from "@/api/notifications";
import { describeError } from "@/lib/errors";

/**
 * Server state for the bell. The count is polled every 30 s on every page
 * (TanStack pauses it while the tab is hidden); the list is fetched only
 * while the popover is open.
 */

export function useNotificationCount(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.count(),
    queryFn: ({ signal }) => getNotificationCount(signal),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useNotificationList(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(),
    queryFn: ({ pageParam, signal }) => getNotifications(pageParam, signal),
    initialPageParam: null as number | null,
    getNextPageParam: (last: NotificationPage) =>
      last.items.length >= NOTIFICATION_PAGE_SIZE
        ? (last.items[last.items.length - 1]?.id ?? null)
        : null,
    enabled,
    staleTime: 5_000,
    // Open, the list follows the bell's own pace.
    refetchInterval: enabled ? 30_000 : false,
  });
}

const refresh = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: notificationKeys.all });

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationsRead,
    onError: (error) => toast.error(describeError(error)),
    onSettled: () => refresh(queryClient),
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotification,
    onError: (error) => toast.error(describeError(error)),
    onSettled: () => refresh(queryClient),
  });
}

/**
 * Tells the person when a follow-up of theirs comes due while the app is
 * open. The count says THAT one more is due; the list says which, so on a rise
 * the first page is fetched and compared with the ids already seen (kept in
 * memory only). The first answer after load only seeds the set — a follow-up
 * that was already due is on the bell, not a pop-up.
 */
export function useFollowUpToasts(
  followUpsDue: number | undefined,
  open: (ticketId: number) => void,
) {
  const queryClient = useQueryClient();
  const seen = useRef<Set<number> | null>(null);
  const lastCount = useRef<number | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (followUpsDue === undefined) return;
    const first = seen.current === null;
    const rose = lastCount.current !== null && followUpsDue > lastCount.current;
    lastCount.current = followUpsDue;
    if (!first && !rose) return;
    if (first && followUpsDue === 0) {
      seen.current = new Set();
      return;
    }
    let cancelled = false;
    void queryClient
      .fetchQuery({
        queryKey: [...notificationKeys.all, "due-probe"],
        queryFn: ({ signal }) => getNotifications(null, signal),
        staleTime: 0,
      })
      .then((page) => {
        if (cancelled) return;
        const known = (seen.current ??= new Set<number>());
        const fresh: FollowUpDue[] = page.followUpsDue.filter(
          (item) => !known.has(item.ticketId),
        );
        for (const item of page.followUpsDue) known.add(item.ticketId);
        if (first) return;
        for (const item of fresh.slice(0, 3)) {
          toast.message(`Follow-up due: ${item.number}`, {
            description: item.personName
              ? `${item.title} · ${item.personName}`
              : item.title,
            action: { label: "Open", onClick: () => openRef.current(item.ticketId) },
          });
        }
        if (fresh.length > 3)
          toast.message(`${fresh.length - 3} more follow-ups are due`);
      })
      .catch(() => {
        // The bell still shows the count; a missed pop-up is not worth an error.
        if (first) seen.current ??= new Set();
      });
    return () => {
      cancelled = true;
    };
  }, [followUpsDue, queryClient]);
}
